import { useEffect, useState } from 'react';
import Cookies from 'js-cookie';
import { CookieManagementCookieSettings, getCookieManagementSettings } from '@/domain/cookie-management';

export type Preferences = {
  trackingCookies: boolean;
  recordingCookies: boolean;
};

const nullPreferences: Preferences = {
  trackingCookies: false,
  recordingCookies: false,
};

export default function useCookiePreferences(): Preferences {
  const [preferences, setPreferences] = useState<Preferences>(() => {
    const initialCookieValue = Cookies.get(CookieManagementCookieSettings.name);
    if (!initialCookieValue) return nullPreferences;
    try {
      const parsed = JSON.parse(initialCookieValue);
      return {
        trackingCookies: !!parsed.trackingCookies,
        recordingCookies: !!parsed.recordingCookies,
      };
    } catch {
      return nullPreferences;
    }
  });

  useEffect(() => {
    let isCurrent = true;

    const loadCookieSettings = async () => {
      const cookieValue = Cookies.get(CookieManagementCookieSettings.name);
      const cookieSettings = await getCookieManagementSettings(cookieValue);
      if (isCurrent)
        setPreferences({
          trackingCookies: cookieSettings.data.trackingCookies,
          recordingCookies: cookieSettings.data.recordingCookies,
        });
    };

    loadCookieSettings();

    const handleCookieEvent = (event: Event) => {
      const cookieEvent = event as CustomEvent<Preferences>;
      if (cookieEvent.detail) {
        setPreferences(cookieEvent.detail);
      }
    };

    window.addEventListener('tc_cookie_manager_updated', handleCookieEvent);
    return () => {
      isCurrent = false;
      window.removeEventListener('tc_cookie_manager_updated', handleCookieEvent);
    };
  }, []);

  return preferences;
}
